import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import type {
  HomePopup,
  HomePopupListQuery,
  HomePopupListResult,
  HomePopupPagination,
  HomePopupPayload,
} from '../types/homePopup';

const BASE = '/admin/v1/home-popups';

/**
 * catalog envelope(`CatalogApiResponse`) — 목록은 `data` 에 Spring `Page` 가 그대로 직렬화되어
 * `data.content` 로 들어오고, 페이지 정보는 `meta.pagination`(★page 는 1-based, `PageMeta.from`)
 * 에 담긴다. 서비스마다 `items`/배열 직접 반환 형태가 섞여 있어 방어적으로 모두 수용한다.
 */
interface ListEnvelope {
  success: boolean;
  data?:
    | {
        content?: HomePopup[];
        items?: HomePopup[];
        totalElements?: number;
        number?: number;
        size?: number;
        totalPages?: number;
      }
    | HomePopup[]
    | null;
  meta?: { pagination?: HomePopupPagination } | null;
}

interface SingleEnvelope {
  success: boolean;
  data?: HomePopup | null;
}

export const homePopupsQueryKey = (query: HomePopupListQuery) =>
  ['admin', 'home-popups', query.page, query.size] as const;

export const homePopupQueryKey = (id: number) => ['admin', 'home-popup', id] as const;

/** BE 기본 정렬이 sortOrder 이므로 클라이언트는 sort 파라미터를 보내지 않는다(BE 순서 그대로 렌더). */
const buildSearchParams = (query: HomePopupListQuery): string =>
  new URLSearchParams({
    page: String(Math.max(0, query.page - 1)),
    size: String(query.size),
  }).toString();

const fetchHomePopups = async (query: HomePopupListQuery): Promise<HomePopupListResult> => {
  const res = await adminFetch(`${BASE}?${buildSearchParams(query)}`);
  if (!res.ok) throw new Error(`home popups list failed: ${res.status}`);
  const env = (await res.json()) as ListEnvelope;
  const d = env.data;
  const items = Array.isArray(d) ? d : (d?.content ?? d?.items ?? []);
  const pagination: HomePopupPagination = env.meta?.pagination ?? {
    page: query.page,
    size: query.size,
    totalItems: Array.isArray(d) ? d.length : (d?.totalElements ?? items.length),
    totalPages: Array.isArray(d) ? 1 : (d?.totalPages ?? 1),
  };
  return { items, pagination };
};

const fetchHomePopup = async (id: number): Promise<HomePopup> => {
  const res = await adminFetch(`${BASE}/${id}`);
  if (!res.ok) throw new Error(`home popup fetch failed: ${res.status}`);
  const env = (await res.json()) as SingleEnvelope;
  if (!env.data) throw new Error('no data');
  return env.data;
};

export const useHomePopupsQuery = (query: HomePopupListQuery) =>
  useQuery<HomePopupListResult>({
    queryKey: homePopupsQueryKey(query),
    queryFn: () => fetchHomePopups(query),
  });

export const useHomePopupQuery = (id: number | null) =>
  useQuery<HomePopup>({
    queryKey: homePopupQueryKey(id ?? -1),
    queryFn: () => fetchHomePopup(id as number),
    enabled: id != null,
  });

export const useHomePopupMutation = () => {
  const qc = useQueryClient();
  const invalidateList = () => qc.invalidateQueries({ queryKey: ['admin', 'home-popups'] });

  const create = useMutation({
    mutationFn: async (payload: HomePopupPayload): Promise<HomePopup> => {
      const res = await adminFetch(BASE, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', BASE, payload) },
        body: JSON.stringify(payload),
      });
      // popupKey 는 UNIQUE(uq_home_popups_key) — 중복이면 BE 가 한국어 메시지로 400 을 준다.
      // 호출부가 friendlyMessage 로 그 본문을 그대로 보여줄 수 있도록 상태코드만 던지지 않는다.
      if (!res.ok) await throwFromEnvelope(res, `home popup create failed: ${res.status}`);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: invalidateList,
  });

  const update = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: HomePopupPayload }): Promise<HomePopup> => {
      const path = `${BASE}/${id}`;
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', path, payload) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) await throwFromEnvelope(res, `home popup update failed: ${res.status}`);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: homePopupQueryKey(id) });
      invalidateList();
    },
  });

  const remove = useMutation({
    mutationFn: async (id: number): Promise<void> => {
      const path = `${BASE}/${id}`;
      const res = await adminFetch(path, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': idempotencyKeyFor('DELETE', path, '') },
      });
      // 204 No Content — 본문이 없으므로 파싱하지 않는다.
      if (!res.ok) await throwFromEnvelope(res, `home popup delete failed: ${res.status}`);
    },
    onSuccess: invalidateList,
  });

  return { create, update, remove };
};

/** 실패 응답의 `error.message`(BE 한국어 문구)를 살려서 던진다. 없으면 fallback. */
async function throwFromEnvelope(res: Response, fallback: string): Promise<never> {
  let message = fallback;
  try {
    const body = (await res.json()) as { error?: { message?: string } | null };
    if (body?.error?.message) message = body.error.message;
  } catch {
    // 본문이 비었거나 JSON 이 아니면 fallback 을 쓴다.
  }
  throw new Error(message);
}
